"use client"
import { useState, useEffect, useCallback } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { API_ENDPOINTS } from "../../../constants/config";

// Helper function to validate year range
const validateYearRange = (startYear, endYear) => {
  if (!startYear || !endYear) return "Both years are required"
  if (startYear < 2000 || endYear > 2100) return "Years must be between 2000 and 2100"
  // Backend enforces: endYear must be exactly startYear + 1
  if (endYear !== startYear + 1) return "End year must be start year + 1"
  return null
}

function AddAcademicYear({ onClose, onSuccess }) {
  const currentYear = new Date().getFullYear()
  const [academicYearData, setAcademicYearData] = useState({
    startYear: currentYear,
    endYear: currentYear + 1,
    isCurrent: false
  })
  const [error, setError] = useState(null)

  const handleStartYearChange = (e) => {
    const startYear = parseInt(e.target.value)
    setAcademicYearData(prev => ({
      ...prev,
      startYear,
      endYear: startYear + 1
    }))
  }

  const handleEndYearChange = (e) => {
    const endYear = parseInt(e.target.value)
    setAcademicYearData(prev => ({
      ...prev,
      endYear
    }))
    setError(validateYearRange(academicYearData.startYear, endYear))
  }

  const handleSubmit = async () => {
    const validationError = validateYearRange(academicYearData.startYear, academicYearData.endYear)
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setError(null)
      await axiosInstance.post(API_ENDPOINTS.academicYears, academicYearData)
      onSuccess()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add academic year")
      console.error("Error adding academic year:", error)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Add New Academic Year</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startYear">Start Year</Label>
              <Input
                id="startYear"
                type="number"
                value={academicYearData.startYear}
                onChange={handleStartYearChange}
                min={2000}
                max={2100}
              />
            </div>
            <div>
              <Label htmlFor="endYear">End Year</Label>
              <Input
                id="endYear"
                type="number"
                value={academicYearData.endYear}
                onChange={handleEndYearChange}
                min={academicYearData.startYear + 1}
                max={2100}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="isCurrent"
              checked={academicYearData.isCurrent}
              onCheckedChange={(checked) => 
                setAcademicYearData(prev => ({ ...prev, isCurrent: checked }))
              }
            />
            <Label htmlFor="isCurrent">Set as Current Academic Year</Label>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Add Academic Year</Button>
        </div>
      </CardContent>
    </Card>
  )
}

function EditAcademicYear({ academicYearData, onClose, onSuccess }) {
  const [academicYear, setAcademicYear] = useState({
    startYear: academicYearData.startYear,
    endYear: academicYearData.endYear,
    isCurrent: academicYearData.isCurrent
  })
  const [error, setError] = useState(null)

  const handleStartYearChange = (e) => {
    const startYear = parseInt(e.target.value)
    setAcademicYear(prev => ({
      ...prev,
      startYear,
      endYear: Math.max(prev.endYear, startYear + 1)
    }))
  }

  const handleEndYearChange = (e) => {
    const endYear = parseInt(e.target.value)
    setAcademicYear(prev => ({
      ...prev,
      endYear
    }))
    setError(validateYearRange(academicYear.startYear, endYear))
  }

  const handleSubmit = async () => {
    const validationError = validateYearRange(academicYear.startYear, academicYear.endYear)
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setError(null)
      const token = localStorage.getItem("token")
      
      await axiosInstance.patch(
        API_ENDPOINTS.academicYear(academicYearData._id),
        academicYear,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )
      onSuccess()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update academic year")
      console.error("Error updating academic year:", error)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Academic Year</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="editStartYear">Start Year</Label>
              <Input
                id="editStartYear"
                type="number"
                value={academicYear.startYear}
                onChange={handleStartYearChange}
                min={2000}
                max={2100}
              />
            </div>
            <div>
              <Label htmlFor="editEndYear">End Year</Label>
              <Input
                id="editEndYear"
                type="number"
                value={academicYear.endYear}
                onChange={handleEndYearChange}
                min={academicYear.startYear + 1}
                max={2100}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="editIsCurrent"
              checked={academicYear.isCurrent}
              onCheckedChange={(checked) => 
                setAcademicYear(prev => ({ ...prev, isCurrent: checked }))
              }
            />
            <Label htmlFor="editIsCurrent">Set as Current Academic Year</Label>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Update Academic Year</Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function AcademicYearList() {
  const [academicYears, setAcademicYears] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null)
  const [error, setError] = useState(null)

  const fetchAcademicYears = useCallback(async () => {
    try {
      setError(null)
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.academicYears, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      setAcademicYears(response.data.data.academicYears)
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch academic years")
      console.error("Error fetching academic years:", error)
    }
  }, [])

  useEffect(() => {
    fetchAcademicYears()
  }, [fetchAcademicYears])

  const handleDelete = async (academicYearId) => {
    if (!window.confirm("Are you sure you want to delete this academic year? This action is irreversible.")) {
      return
    }

    try {
      setError(null)
      await axiosInstance.delete(API_ENDPOINTS.academicYear(academicYearId))
      fetchAcademicYears()
    } catch (error) {
      // If backend returns dependentCounts, show a friendly message listing counts
      const resp = error.response?.data
      if (resp && resp.dependentCounts) {
        const parts = Object.entries(resp.dependentCounts).map(([k, v]) => `${k}: ${v}`).join("; ")
        setError(`Cannot delete academic year — dependent records exist: ${parts}`)
      } else {
        setError(resp?.message || "Failed to delete academic year")
      }
      console.error("Error deleting academic year:", error)
    }
  }

  return (
    <Card className="w-full">
      <CardContent className="p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-6 gap-2 sm:gap-0">
  <Button variant="outline" onClick={fetchAcademicYears}>
    Refresh List
  </Button>
  <Button onClick={() => setShowAddModal(true)}>Add New Academic Year</Button>
</div>


        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Start Year</TableHead>
              <TableHead>End Year</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {academicYears.map((year) => (
              <TableRow key={year._id}>
                <TableCell>{year.startYear}</TableCell>
                <TableCell>{year.endYear}</TableCell>
                <TableCell>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${
                    year.isCurrent 
                      ? "bg-green-100 text-green-800" 
                      : "bg-gray-100 text-gray-800"
                  }`}>
                    {year.isCurrent ? "Current" : "Not Current"}
                  </div>
                </TableCell>
                <TableCell>{new Date(year.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedAcademicYear(year)
                        setShowEditModal(true)
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(year._id)}
                      disabled={year.isCurrent}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {showAddModal && (
          <AddAcademicYear
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false)
              fetchAcademicYears()
            }}
          />
        )}

        {showEditModal && selectedAcademicYear && (
          <EditAcademicYear
            academicYearData={selectedAcademicYear}
            onClose={() => {
              setShowEditModal(false)
              setSelectedAcademicYear(null)
            }}
            onSuccess={() => {
              setShowEditModal(false)
              setSelectedAcademicYear(null)
              fetchAcademicYears()
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}